import { useContext, useEffect, useRef, useState } from 'react'
import { State } from '../utils/context'
import { normalize } from '../utils/books'
import Options from './options'
import ArrowDown from '../icons/arrow-down'
import styles from './select.module.css'

// Pozycja pasuje, gdy któreś jej słowo zaczyna się od wpisanego tekstu, np. „pie” → „Pieśń nad pieśniami”.
export const matchText = (query, id, option) => {
  const words = normalize(String(option)).split(' ')

  return words.some((_, index) => words.slice(index).join(' ').startsWith(normalize(query.trim())))
}

const Select = props => {
  const [{input, status, active}, setState] = useContext(State)
  const [query, setQuery] = useState('')
  const self = useRef()
  const field = useRef()
  const button = useRef()
  const opened = useRef(false)
  const pressed = useRef(-Infinity)
  const left = useRef(false)
  const open = !!active.control[props.name]

  // Po wpisaniu tekstu lista pokazuje tylko pasujące pozycje, np. „kor” → 1 i 2 Koryntian.
  const options = query.trim() ? Object.fromEntries(Object.entries(props.options).filter(([id, option]) => (
    (props.match ?? matchText)(query, id, option)
  ))) : props.options

  const onClick = () => setState(state => ({
    ...state,
    active: {
      ...state.active,
      control: {
        [props.name]: !state.active.control[props.name]
      }
    }
  }))

  const onClose = () => setState(state => state.active.control[props.name] ? {
    ...state,
    active: {
      ...state.active,
      control: {}
    }
  } : state)

  // Fokus wychodzący poza select (np. Tab za ostatnią opcją, „Gotowe” na iOS) zamyka listę, żeby nie
  // trafiał na elementy strony schowane pod nią. Z wpisanym tekstem zatwierdza wybór jak Enter –
  // klawiatura numeryczna na iOS nie ma Entera. Na iOS przyciski nie dostają fokusu, a pole traci go
  // dopiero po puszczeniu palca, tuż przed kliknięciem – dlatego dotyk wewnątrz selecta (`pressed`)
  // trwa do kliknięcia (albo chwilę po `pointerup`, gdyby kliknięcia nie było). Przełączenie karty lub aplikacji (`hasFocus`)
  // zostawia select bez zmian.
  const onBlur = e => {
    if (!open || performance.now() - pressed.current < 500 || self.current.contains(e.relatedTarget) || !document.hasFocus()) return

    left.current = true
    e.target == field.current && query.trim() && onSubmit() || onClose()
  }

  // W trakcie dotyku `Infinity`, po puszczeniu palca jego czas, a po kliknięciu lub przerwaniu `-Infinity`.
  const onPress = value => () => pressed.current = value == 'up' ? performance.now() : value

  const onChange = value => setState(state => ({
    ...state,
    input: {
      ...state.input,
      chapter: value.hasOwnProperty('book') ? 1 : state.input.chapter,
      ...value
    }
  }))

  // Enter wybiera najlepsze dopasowanie (np. dokładną siglę), a gdy go nie ma – pierwszą pozycję listy.
  const onSubmit = () => {
    const best = props.best?.(query)
    const id = best != null && best in options ? best : Object.keys(options)[0]

    id != null && onChange({[props.name]: +id})

    return id != null
  }

  // Strzałki ↑/↓ przechodzą między polem a pozycjami listy.
  const onKeyDown = e => {
    if (!open) return

    if (e.key == 'Enter' && e.target == field.current) {
      e.preventDefault()
      onSubmit()
    }
    else if (e.key == 'ArrowDown' || e.key == 'ArrowUp') {
      const items = [field.current, ...self.current.querySelectorAll('[data-option]')]
      const index = items.indexOf(e.target)

      if (index < 0) return

      e.preventDefault()
      items[Math.min(Math.max(0, index + (e.key == 'ArrowDown' ? 1 : -1)), items.length - 1)].focus()
    }
  }

  useEffect(() => {
    if (open) {
      field.current.focus({preventScroll: true})
    }
    else {
      setQuery('')

      // Po zamknięciu listy fokus wraca na select, żeby nie przepadł razem z polem – chyba że
      // użytkownik sam z niego wyszedł (np. „Gotowe” na iOS).
      opened.current && !left.current && document.activeElement == document.body && button.current?.focus()
    }

    opened.current = open
    left.current = false
  }, [open])

  return (
    <div
      className={styles.select}
      ref={self}
      hidden={active.search}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onPointerDown={onPress(Infinity)}
      onPointerUp={onPress('up')}
      onPointerCancel={onPress(-Infinity)}
      onClickCapture={onPress(-Infinity)}
    >
      {/* Obramowanie jest jednym elementem w obu stanach, żeby tło nie migało przy podmianie zawartości. */}
      <div className={`${styles.value} ${open ? styles.open : ''}`}>
        {open ? (
          <>
            <input
              className={styles.input}
              ref={field}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={props.options[input[props.name]]}
              autoComplete="off"
              spellCheck={false}
              inputMode={props.inputMode}
              enterKeyHint="go"
              role="combobox"
              aria-expanded="true"
              aria-autocomplete="list"
              aria-label={props.label}
            />
            <button
              className={styles.toggle}
              onClick={onClick}
              aria-label="Close"
            >
              <ArrowDown className={styles.icon} />
            </button>
          </>
        ) : (
          <button
            className={styles.trigger}
            ref={button}
            onClick={onClick}
            tabIndex={status == 'loading' ? -1 : null}
            aria-expanded="false"
            aria-disabled={status == 'loading' ? true : null}
          >
            <div className={styles.text}>{props.options[input[props.name]]}</div>
            <ArrowDown className={styles.icon} />
          </button>
        )}
      </div>
      <Options
        options={options}
        value={input[props.name]}
        query={query}
        active={open}
        onChange={id => onChange({[props.name]: +id})}
      />
    </div>
  )
}

export default Select
